/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { CommandBarButton } from '@fluentui/react/lib/Button';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisCommandBar } from 'src/core/components/LuisCommandBar';
import { LuisTooltip } from 'src/core/components/LuisTooltip';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { TableCommandBarProps } from 'src/core/components/TableCommandBarProps';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getCommandBarButtonStyles } from 'src/core/utils/azureRbacStyles';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { commandBarItemSelectionManager } from 'src/core/utils/commandBarItemSelectionManager';
import { FeatureToggles } from 'src/core/utils/featureToggles';
import { ModelDetailsObject } from 'src/modules/modelDetails/components/ModelDetailsListTable';

export type ModelDetailsListCommandBarProps = TableCommandBarProps<ModelDetailsObject> & {
    onDeleteModels: () => boolean | void;
    onExportModel: () => boolean | void;
    onLoadModel?: () => boolean | void;
    onRefreshEvaluation?: () => boolean | void;
    isEvaluationOrTrainingRunning?: boolean;
};

const messages = defineMessages({
    delete: { id: 'modelDetailsListCommandBar.delete', defaultMessage: 'Delete' },
    searchPlaceholder: { id: 'modelDetailsListCommandBar.searchPlaceholder', defaultMessage: 'Search' },
    loadModelData: { id: 'modelDetailsListCommandBar.loadModelData', defaultMessage: 'Load model data' },
    exportModelData: { id: 'modelDetailsListCommandBar.exportModelData', defaultMessage: 'Export model data' },
    refreshEvaluation: { id: 'modelDetailsListCommandBar.refreshEvaluation', defaultMessage: 'Run evaluation' },
    noSnapshotTooltip: {
        id: 'modelDetailsListCommandBar.noSnapshotTooltip',
        defaultMessage: "This model doesn't have a snapshot. Please retrain this model."
    }
});

/**
 * Represents a command bar for the model details pages.
 */
export const ModelDetailsListCommandBar = observer((props: ModelDetailsListCommandBarProps) => {
    const {
        selectedItems = [],
        query = '',
        onQueryChange,
        onDeleteModels,
        onExportModel,
        onLoadModel,
        onRefreshEvaluation,
        isEvaluationOrTrainingRunning
    } = props;
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();

    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canWrite, canRead } = mapUserRoleToUserActions(userRole);

    const commands: ICommandBarItemProps[] = [
        onLoadModel && {
            key: 'loadModel',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    hidden={canWrite || item.disabled}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="loadModelButtonLanguageToolTip"
                    componentSurroundedByToolTip={
                        <LuisTooltip
                            hidden={selectedItems.every(model => model.hasSnapshot)}
                            content={localize(messages.noSnapshotTooltip)}>
                            <CommandBarButton
                                onClick={onLoadModel}
                                text={localize(messages.loadModelData)}
                                name={localize(messages.loadModelData)}
                                iconProps={{ iconName: 'Upload' }}
                                disabled={!canWrite || item.disabled || !selectedItems.every(model => model.hasSnapshot)}
                                data-automation-id="loadModelButton"
                                styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                            />
                        </LuisTooltip>
                    }
                />
            )
        },
        {
            key: 'exportModel',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    hidden={canRead || item.disabled}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="exportModelButtonLanguageToolTip"
                    componentSurroundedByToolTip={
                        <LuisTooltip
                            hidden={selectedItems.every(model => model.hasSnapshot)}
                            content={localize(messages.noSnapshotTooltip)}>
                            <CommandBarButton
                                onClick={onExportModel}
                                text={localize(messages.exportModelData)}
                                name={localize(messages.exportModelData)}
                                iconProps={{ iconName: 'Download' }}
                                disabled={!canWrite || item.disabled || !selectedItems.every(model => model.hasSnapshot)}
                                data-automation-id="exportModelButton"
                                styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                            />
                        </LuisTooltip>
                    }
                />
            )
        },
        FeatureToggles.EvaluationOnDemand && {
            key: 'refreshEvaluation',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    hidden={canRead || item.disabled}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="refreshEvaluationButtonLanguageToolTip"
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            onClick={onRefreshEvaluation}
                            text={localize(messages.refreshEvaluation)}
                            name={localize(messages.refreshEvaluation)}
                            iconProps={{ iconName: 'TestPlan' }}
                            disabled={!canWrite || item.disabled || isEvaluationOrTrainingRunning}
                            data-automation-id="refreshEvaluationButton"
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                        />
                    }
                />
            )
        },
        {
            key: 'delete',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    hidden={canWrite || item.disabled}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="deleteModelsButtonLanguageToolTip"
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            onClick={onDeleteModels}
                            text={localize(messages.delete)}
                            name={localize(messages.delete)}
                            iconProps={{ iconName: 'Delete' }}
                            disabled={!canWrite || item.disabled}
                            data-automation-id="deleteModelsButton"
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                        />
                    }
                />
            )
        }
    ].filter(Boolean);

    const items: ICommandBarItemProps[] = commandBarItemSelectionManager(
        commands,
        [],
        ['delete', 'exportModel', onLoadModel && 'loadModel', onRefreshEvaluation && 'refreshEvaluation'].filter(Boolean),
        ['delete'],
        selectedItems.length
    );

    return <LuisCommandBar items={items} includeSearchBar searchBarAlwaysExpanded query={query} onQueryChange={onQueryChange} />;
});
